import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GlassCard } from "@/components/GlassCard";
import { useLanguage } from "@/i18n/LanguageContext";
import { GOALS, ACTIVITY_TYPES, LEVELS } from "@/lib/constants";

const selectTriggerClass =
  "h-11 rounded-xl bg-white/5 border-white/10 text-white focus:ring-2 focus:ring-cyan-300/60";

const inputClass =
  "mt-2 h-11 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/35 focus-visible:ring-2 focus-visible:ring-cyan-300/60";

const FieldLabel = ({ children }) => (
  <label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
    {children}
  </label>
);

// options: [{ value: canonical English value, label: translated label }]
const SelectField = ({ label, value, onChange, options, placeholder, testId }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className={`mt-2 ${selectTriggerClass}`}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

export const ProfileForm = ({ profile, onSave }) => {
  const { t } = useLanguage();
  const [name, setName] = useState(profile.name || "");
  const [goal, setGoal] = useState(profile.goal);
  const [preferred, setPreferred] = useState(profile.preferred_activity || "");
  const [level, setLevel] = useState(profile.level);
  const [weeklyGoal, setWeeklyGoal] = useState(profile.weekly_goal_minutes);
  // Stage 2.1: community profile fields
  const [displayName, setDisplayName] = useState(profile.display_name || "");
  const [bio, setBio] = useState(profile.bio || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const wg = parseInt(weeklyGoal, 10);
    if (!wg || wg < 10 || wg > 5000) {
      toast.error(t("profile.weeklyGoalError"));
      return;
    }
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        goal,
        preferred_activity: preferred || null,
        level,
        weekly_goal_minutes: wg,
        display_name: displayName.trim(),
        bio: bio.trim(),
      });
      toast.success(t("profile.saved"));
    } catch {
      toast.error(t("profile.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <GlassCard className="p-5 space-y-5">
      <div>
        <FieldLabel>{t("profile.name")}</FieldLabel>
        <Input
          data-testid="profile-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("profile.namePlaceholder")}
          maxLength={40}
          className={inputClass}
        />
      </div>

      <SelectField
        label={t("profile.mainGoal")}
        value={goal}
        onChange={setGoal}
        options={GOALS.map((g) => ({ value: g, label: t(`goals.${g}`) }))}
        placeholder={t("profile.goalPlaceholder")}
        testId="profile-goal-select"
      />

      <SelectField
        label={t("profile.preferredActivity")}
        value={preferred}
        onChange={setPreferred}
        options={ACTIVITY_TYPES.map(({ id }) => ({ value: id, label: t(`types.${id}`) }))}
        placeholder={t("profile.activityPlaceholder")}
        testId="profile-preferred-activity-select"
      />

      <SelectField
        label={t("profile.level")}
        value={level}
        onChange={setLevel}
        options={LEVELS.map((l) => ({ value: l, label: t(`levels.${l}`) }))}
        placeholder={t("profile.levelPlaceholder")}
        testId="profile-level-select"
      />

      <div>
        <FieldLabel>{t("profile.weeklyGoalMinutes")}</FieldLabel>
        <Input
          data-testid="profile-weekly-goal-input"
          type="number"
          min={10}
          max={5000}
          value={weeklyGoal}
          onChange={(e) => setWeeklyGoal(e.target.value)}
          className={inputClass}
        />
      </div>

      {/* Stage 2.1: community profile section */}
      <div className="pt-4 border-t border-white/10">
        <div className="flex items-center gap-2 mb-4">
          <Users size={14} className="text-cyan-300/80" />
          <p className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
            {t("profile.communitySection")}
          </p>
        </div>
        <div className="space-y-4">
          <div>
            <FieldLabel>{t("profile.displayName")}</FieldLabel>
            <Input
              data-testid="profile-display-name-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t("profile.displayNamePlaceholder")}
              maxLength={40}
              className={inputClass}
            />
          </div>
          <div>
            <FieldLabel>{t("profile.bioLabel")}</FieldLabel>
            <Textarea
              data-testid="profile-bio-input"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder={t("profile.bioPlaceholder")}
              maxLength={140}
              rows={2}
              className="mt-2 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/35 focus-visible:ring-2 focus-visible:ring-cyan-300/60 resize-none"
            />
          </div>
        </div>
      </div>

      <Button
        data-testid="profile-save-button"
        onClick={handleSave}
        disabled={saving}
        className="w-full py-5 rounded-xl bg-cyan-300 text-slate-950 font-semibold hover:bg-cyan-200 active:scale-[0.98] disabled:opacity-60"
      >
        {saving ? <Loader2 className="animate-spin" size={18} /> : t("profile.save")}
      </Button>
    </GlassCard>
  );
};
